// Supabase Schema Viewer Component
// Displays user's database tables and columns in a clean UI

import { useState, useEffect, useCallback } from 'react';
import { Table2, ChevronRight, ChevronDown, RefreshCw, Database, Key, Hash, Calendar, Type, ToggleLeft } from 'lucide-react';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';

interface Column {
    table_name: string;
    column_name: string;
    data_type: string;
    is_nullable: string;
    column_default: string | null;
}

interface TableSchema {
    name: string;
    columns: Column[];
    isExpanded: boolean;
}

interface SupabaseSchemaViewerProps {
    pixelProjectId?: string;
}

export function SupabaseSchemaViewer({ pixelProjectId }: SupabaseSchemaViewerProps) {
    const { getSchema, isConnected } = useSupabaseMCP(pixelProjectId);
    const [tables, setTables] = useState<TableSchema[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadSchema = useCallback(async () => {
        if (!isConnected) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await getSchema();
            if (result.success && result.data) {
                // Group columns by table
                const columnsData = result.data as Column[];
                const tableMap = new Map<string, Column[]>();

                columnsData.forEach((col) => {
                    if (!tableMap.has(col.table_name)) {
                        tableMap.set(col.table_name, []);
                    }
                    tableMap.get(col.table_name)!.push(col);
                });

                const tableSchemas: TableSchema[] = Array.from(tableMap.entries()).map(([name, columns]) => ({
                    name,
                    columns,
                    isExpanded: false,
                }));

                setTables(tableSchemas.sort((a, b) => a.name.localeCompare(b.name)));
            } else {
                setError(result.error || 'Failed to load schema');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [isConnected, getSchema]);

    useEffect(() => {
        if (isConnected) {
            loadSchema();
        }
    }, [isConnected, loadSchema]);

    const toggleTable = (tableName: string) => {
        setTables((prev) =>
            prev.map((t) =>
                t.name === tableName ? { ...t, isExpanded: !t.isExpanded } : t
            )
        );
    };

    const getTypeIcon = (dataType: string) => {
        const lowerType = dataType.toLowerCase();
        if (lowerType.includes('uuid')) return <Key className="h-3 w-3 text-faint" />;
        if (lowerType.includes('int') || lowerType.includes('numeric') || lowerType.includes('decimal')) return <Hash className="h-3 w-3 text-faint" />;
        if (lowerType.includes('timestamp') || lowerType.includes('date') || lowerType.includes('time')) return <Calendar className="h-3 w-3 text-faint" />;
        if (lowerType.includes('bool')) return <ToggleLeft className="h-3 w-3 text-faint" />;
        return <Type className="h-3 w-3 text-faint" />;
    };

    if (!isConnected) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                <PxMark state="idle" size={24} className="mb-4" />
                <h3 className="mb-2 text-[16px] font-medium text-foreground">Supabase non connecté</h3>
                <p className="text-sm text-muted-foreground">
                    Connectez votre compte Supabase via le bouton dans la sidebar.
                </p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-4">
                <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-[13px] font-medium text-foreground">Schéma de la base</h2>
                    <span className="font-mono text-[11px] text-faint">({tables.length} tables)</span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadSchema}
                    disabled={isLoading}
                    className="gap-2"
                >
                    <RefreshCw className="h-4 w-4" />
                    Actualiser
                </Button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
                {isLoading && tables.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                        <PxMark state="thinking" size={20} />
                    </div>
                ) : error ? (
                    <div className="m-4 border-l-2 border-err bg-err/5 p-3 font-mono text-[12px] text-foreground">{error}</div>
                ) : tables.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        Aucune table trouvée dans le schéma public.
                    </div>
                ) : (
                    <div className="p-2">
                        {tables.map((table) => (
                            <div key={table.name} className="mb-1">
                                {/* Table header */}
                                <button
                                    onClick={() => toggleTable(table.name)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-secondary"
                                >
                                    {table.isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <Table2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-mono text-[13px] font-medium text-foreground">{table.name}</span>
                                    <span className="font-mono text-[11px] text-faint">({table.columns.length} col.)</span>
                                </button>

                                {/* Columns */}
                                {table.isExpanded && (
                                    <div className="ml-6 border-l border-border pl-4">
                                        {table.columns.map((col) => (
                                            <div
                                                key={`${table.name}-${col.column_name}`}
                                                className="flex items-center gap-2 px-3 py-1.5 text-sm"
                                            >
                                                {getTypeIcon(col.data_type)}
                                                <span className="font-mono text-[13px] text-foreground">{col.column_name}</span>
                                                <span className="bg-surface-3 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                                                    {col.data_type}
                                                </span>
                                                {col.is_nullable === 'NO' && (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-faint">Requis</span>
                                                )}
                                                {col.column_default && (
                                                    <span className="max-w-32 truncate font-mono text-[11px] text-faint" title={col.column_default}>
                                                        = {col.column_default.slice(0, 20)}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

export default SupabaseSchemaViewer;
